import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import {
  IconArrowsUpDown, IconBell, IconChecklist, IconChevronDown, IconChevronLeft, IconChevronRight, IconCopy, IconDots, IconFileText, IconHistory, IconInfoCircle,
  IconLink, IconListDetails, IconMessageCircle, IconPrinter, IconShieldCheck, IconUserPlus, IconUserShield, IconRoute,
} from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { DECISION_STATUS, decisionTypeLabel } from './decision';
import { decisionView, type CheckLevel } from './decisionDetail';
import s from './profile.module.css';
import d from './decisionDetail.module.css';

// Mint-HRDecision · page=detail (design-specs/Mint-HRDecision.md + code board khối D21-NB). Chỉ đọc; nút ghi báo toast. Không lương: loại có tiền che số.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const TONE = { warn: s.tWarn ?? '', ok: s.tOk ?? '', err: s.tErr ?? '', mute: s.tMute ?? '' };
const LV: Record<CheckLevel, { ic: string; cls: string }> = { ok: { ic: '✓', cls: d.lvOk ?? '' }, warn: { ic: '!', cls: d.lvWarn ?? '' }, err: { ic: '×', cls: d.lvErr ?? '' } };
const VT = { warn: d.vWarn, ok: d.vOk, err: d.vErr } as const;
const ini = (u: string) => u.slice(0, 2).toUpperCase();

function Card({ title, tip, icon, extra, open, onToggle, children }: {
  title: string; tip: string; icon: ReactNode; extra?: ReactNode; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.hd}>
        <span className={s.hdIc} aria-hidden="true">{icon}</span>
        <h2>{title}</h2>
        <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {extra}
        <div className={s.tools}>
          <button type="button" className={s.tog} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={onToggle}>
            <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

export function HrDecisionDetail() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { no } = useParams({ strict: false }) as { no?: string };
  const { data, error, isLoading, isError, refetch } = useList('hrDecision', { limit: 100 });
  const forced = readState();
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [openC, setOpenC] = useState<Record<string, boolean>>({ sum: true, doc: true, chk: true, apl: true, act: true, todo: true, info: true, steps: true, ppl: true, links: false });
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [allAct, setAllAct] = useState(false);
  const [rj, setRj] = useState<{ open: boolean; text: string; tried: boolean }>({ open: false, text: '', tried: false });
  const tog = (k: string) => () => setOpenC((o) => ({ ...o, [k]: !o[k] }));
  const noWrite = (m?: string) => toast({ msg: m ? `${m} · chưa mở ghi ở bản này` : t('hr.s.noWrite') });
  const back = () => void navigate({ to: '/ezy/hr/decision' });

  const all = data?.items ?? [];
  const dec = all.find((x) => x.no === no);
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!dec) state = 'empty';

  const v = dec ? decisionView(dec) : null;
  const st = dec ? DECISION_STATUS[dec.status] : null;
  const nWarn = v ? v.checks.filter((c) => c.lv !== 'ok').length : 0;
  const openN = v ? v.todos.filter((t) => !done[t.id]).length : 0;
  const acts = v?.acts ?? [];

  const primaryClick = () => {
    if (!v?.primary) return;
    if (v.primary.kind === 'approve') noWrite('Duyệt & áp vào hồ sơ');
    else noWrite(v.primary.label);
  };
  const print = () => toast({ msg: `Đang in ${dec?.no ?? ''} · PDF vector, Inter nhúng` });

  return (
    <Page kind="detail">
      <PageHeader
        title={dec?.no ?? 'Quyết định nhân sự'}
        lead={<button type="button" className={s.back} aria-label="Về sổ quyết định" title="Về sổ quyết định" onClick={back}><IconChevronLeft size={20} stroke={2} aria-hidden="true" /></button>}
        meta={dec && st ? (
          <>
            <span className={`${s.note} ${TONE[st.tone]}`}>{st.label}</span>
            <span className={s.note}>{decisionTypeLabel(dec.type)}</span>
          </>
        ) : undefined}
        sub={dec ? `${dec.name} · ${dec.seat}` : undefined}
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value={dense} onChange={setDense} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <HeaderTool label="Thông báo · 4 mới" icon={IconBell} n={4} onClick={() => toast({ msg: '4 thông báo mới · mở ở Việc & Duyệt' })} />
            <HeaderTool label="Thêm thao tác" icon={IconDots} onClick={() => noWrite('Thêm thao tác')} />
            {v && <Button variant="primary" onClick={primaryClick}>{v.primary?.label ?? 'Xem bản in'}</Button>}
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Không tìm thấy quyết định' : undefined}
        desc={state === 'empty' ? 'Số quyết định không có trong sổ.' : undefined}
        action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hr.s.retry')}</Button> : state === 'empty' ? <Button onClick={back}>{t('hr.s.backDecisions')}</Button> : undefined}
        skeleton={<Skeleton rows={10} />}
      >
        {dec && v && (
          <div className={s.grid}>
            <div className={s.col}>
              <Card title="Tóm tắt" tip="Người được quyết định, loại, hiệu lực và thay đổi trước → sau. Nội dung có số tiền che khi không có quyền nhóm lương." icon={<IconListDetails size={18} stroke={1.9} />} open={!!openC.sum} onToggle={tog('sum')}>
                <div className={s.me}>
                  <span className={s.avM} style={{ width: 44, height: 44, fontSize: 15 }} aria-hidden="true">{ini(dec.user)}</span>
                  <div className={s.who} style={{ flex: '1 1 0' }}>
                    <b>{dec.name}</b>
                    <div><span>{dec.user} · {dec.seat}</span></div>
                  </div>
                  <span className={s.note}>{decisionTypeLabel(dec.type)}</span>
                </div>
                <div className={d.ba}>
                  <div className={d.bef}><div>{t('hr.s.current')}</div><div className={v.masked ? d.mask : undefined}>{v.before}</div></div>
                  <span className={d.arrow} aria-hidden="true">→</span>
                  <div className={d.aft}><div>Sau quyết định · hiệu lực {dec.effective}</div><div className={v.masked ? d.mask : undefined}>{v.after}</div></div>
                </div>
                {dec.status === 'Rejected' && <div className={d.reason}>Lý do từ chối: {dec.reason ?? '—'}</div>}
                {rj.open && (
                  <div className={d.rj} role="dialog" aria-label="Lý do từ chối">
                    <label>
                      <div>{t('hr.s.rejectReason')} <span>*</span></div>
                      <textarea aria-label="Lý do từ chối" rows={2} aria-invalid={rj.tried && !rj.text.trim()} value={rj.text} onChange={(e) => setRj((r) => ({ ...r, text: e.target.value }))} />
                    </label>
                    <div className={d.rjBtns}>
                      <Button onClick={() => setRj({ open: false, text: '', tried: false })}>{t('hr.s.cancel')}</Button>
                      <Button variant="primary" onClick={() => { if (!rj.text.trim()) setRj((r) => ({ ...r, tried: true })); else { setRj({ open: false, text: '', tried: false }); noWrite('Từ chối'); } }}>{t('hr.s.sendReject')}</Button>
                    </div>
                  </div>
                )}
              </Card>
              <Card title="Văn bản quyết định" tip="Văn bản ghép từ mẫu (TblContractTemplate). Bản in PDF vector (PrintKit, Inter nhúng) mở bằng FilePreview." icon={<IconFileText size={18} stroke={1.9} />} open={!!openC.doc} onToggle={tog('doc')}>
                <div className={d.doc}>
                  <span className={d.docIc} aria-hidden="true"><IconFileText size={18} stroke={1.9} /></span>
                  <span className={d.docTx}><span>{v.doc.title}</span><span>{v.doc.sub}</span></span>
                  <Button icon={<IconPrinter size={16} aria-hidden="true" />} onClick={print}>{t('hr.s.viewPrint')}</Button>
                </div>
              </Card>
              <Card title="Kiểm tự động" tip="Đối chiếu khung bậc, định biên ghế, trường bắt buộc của mẫu trước khi duyệt." icon={<IconShieldCheck size={18} stroke={1.9} />} extra={nWarn ? <span className={s.badge}>{nWarn} cần xem</span> : undefined} open={!!openC.chk} onToggle={tog('chk')}>
                {v.checks.map((c) => (
                  <div key={c.k} className={d.chk}><span className={`${d.chkIc} ${LV[c.lv].cls}`} aria-hidden="true">{LV[c.lv].ic}</span><span>{c.k}</span></div>
                ))}
              </Card>
              <Card title="Tự áp vào hồ sơ" tip="Khi duyệt, hệ thống tự áp các thay đổi vào hồ sơ người được quyết định từ ngày hiệu lực. Không hoàn tác — muốn đổi phải lập quyết định mới." icon={<IconArrowsUpDown size={18} stroke={1.9} />} open={!!openC.apl} onToggle={tog('apl')}>
                <div className={d.apK}>{v.applyK}</div>
                {v.apply.map((a) => <div key={a.k} className={d.apRow}><span>{a.k}</span><b>{a.v}</b></div>)}
                {v.seatLink && <button type="button" className={d.seatLink} onClick={() => void navigate({ to: '/ezy/hr' })}><IconUserPlus size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.assignSeat')}</button>}
              </Card>
              <Card title="Hoạt động" tip="Nhật ký quyết định: lập, gửi duyệt, duyệt / từ chối, áp vào hồ sơ, in văn bản." icon={<IconHistory size={18} stroke={1.9} />} extra={<span className={s.cnt}>{acts.length} mục</span>} open={!!openC.act} onToggle={tog('act')}>
                {(allAct ? acts : acts.slice(0, 5)).map((a) => (
                  <div key={a.k} className={s.actRow}>
                    <span className={s.avS} aria-hidden="true">{ini(a.who)}</span>
                    <span className={s.actTx}><span title={a.k}>{a.k}</span><span>{a.who} · {a.when}</span></span>
                    <span className={s.kind}>{a.kind}</span>
                  </div>
                ))}
                {acts.length > 5 && <button type="button" className={d.more} onClick={() => setAllAct((x) => !x)}>{allAct ? 'Thu gọn' : `Xem thêm ${acts.length - 5}`}</button>}
              </Card>
            </div>
            <div className={s.col}>
              <Card title="Việc cần làm" tip="Việc sinh từ quyết định: duyệt / gửi duyệt, các mục kiểm cần xử lý trước ngày hiệu lực. Tick để đánh dấu xong." icon={<IconChecklist size={18} stroke={1.9} />} extra={openN ? <span className={s.badge}>{openN}</span> : undefined} open={!!openC.todo} onToggle={tog('todo')}>
                {v.todos.map((t) => (
                  <div key={t.id} className={s.todo} style={done[t.id] ? { opacity: 0.55 } : undefined}>
                    <button type="button" className={s.chk} role="checkbox" aria-checked={!!done[t.id]} aria-label={`Xong: ${t.k}`} onClick={() => setDone((x) => ({ ...x, [t.id]: !x[t.id] }))}><span>{done[t.id] ? '✓' : ''}</span></button>
                    <span className={s.todoTx}>
                      <span title={t.k}>{t.k}</span>
                      <span><span className={s.avT} aria-hidden="true">{ini(t.who)}</span><span>{t.who} ·</span><span className={t.tone === 'err' ? d.vErr : d.vWarn}>{t.due}</span></span>
                    </span>
                    {t.act && !done[t.id] && <button type="button" className={s.act} onClick={() => (t.act === 'Duyệt' ? primaryClick() : noWrite(t.act))}>{t.act}</button>}
                  </div>
                ))}
                {!v.todos.length && <div className={s.none}>{t('hr.s.noTodo')}</div>}
              </Card>
              <Card title="Thông tin chính" tip="Số quyết định, mẫu, người được quyết định, trạng thái và thời điểm lập." icon={<IconFileText size={18} stroke={1.9} />} open={!!openC.info} onToggle={tog('info')}>
                {v.info.map((x) => (
                  <div key={x.k} className={s.info}>
                    <span>{x.k}</span>
                    {x.href
                      ? <button type="button" className={d.ln} style={{ flexGrow: 1 }} onClick={() => void navigate({ to: '/ezy/hr/profile/$staffId', params: { staffId: dec.user } })}>{x.v} ↗</button>
                      : <span className={x.tone ? VT[x.tone] : undefined}>{x.v}</span>}
                    {x.copy && <button type="button" className={s.copy} aria-label={`Chép ${x.k}`} title="Chép" onClick={() => { void navigator.clipboard?.writeText(x.v); toast({ msg: `Đã chép ${x.k}` }); }}><IconCopy size={16} stroke={1.9} aria-hidden="true" /></button>}
                  </div>
                ))}
              </Card>
              <Card title="Các bước duyệt theo ghế" tip="Người lập và người duyệt lấy theo ghế đang giữ (TblOrgAssign). Không ai tự duyệt quyết định của chính mình hay do mình lập. HRD-MGR lập → BOD-DIR duyệt." icon={<IconRoute size={18} stroke={1.9} />} extra={<span className={s.cnt}>{v.steps.filter((x) => x.st === 'done').length}/{v.steps.length}</span>} open={!!openC.steps} onToggle={tog('steps')}>
                <div className={d.stepW}>
                  {v.steps.map((x, i) => (
                    <div key={x.seat} className={d.step}>
                      <div className={d.stepRail}>
                        <span className={`${d.stepN} ${d[x.st] ?? ''}`}>{x.st === 'done' ? '✓' : i + 1}</span>
                        {i < v.steps.length - 1 && <span className={d.stepLine} />}
                      </div>
                      <div className={d.stepBody}>
                        <span>{x.seat}</span>
                        <span className={d.stepWho}><span className={s.avT} aria-hidden="true">{ini(x.who)}</span><b>{x.who}</b><span className={`${d.stepSt} ${d[x.st] ?? ''}`}>{x.stK}</span></span>
                        <span className={d.stepSub}>{x.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title="Người phụ trách" tip="Avatar lấy từ TblStaff.ImageProfile (HanaAvatar), chưa có ảnh thì hiện chữ cái. Người duyệt hiện theo ghế (TblOrgAssign)." icon={<IconUserShield size={18} stroke={1.9} />} open={!!openC.ppl} onToggle={tog('ppl')}>
                {v.people.map((u) => (
                  <div key={u.u} className={s.ppl}>
                    <span className={s.avM} aria-hidden="true">{ini(u.u)}</span>
                    <span className={s.pplTx}><b>{u.u}</b><span>{u.role}</span></span>
                    {u.hei && <a className={s.hei} href="/hei/" target="_blank" rel="noopener" aria-label={`Nhắn ${u.u} trong HANA Hei`} title="Nhắn trong HANA Hei"><IconMessageCircle size={18} stroke={1.9} aria-hidden="true" /></a>}
                  </div>
                ))}
              </Card>
              <Card title="Liên kết" tip="Lối sang màn liên quan. Mở trong cùng HANA Ezy, giữ ngữ cảnh bản ghi." icon={<IconLink size={18} stroke={1.9} />} open={!!openC.links} onToggle={tog('links')}>
                {([
                  [`Hồ sơ ${dec.user}`, 'Hồ sơ nhân sự · tab Quyết định', () => void navigate({ to: '/ezy/hr/profile/$staffId', params: { staffId: dec.user } })],
                  ['Mẫu văn bản', 'Quản lý ở Hợp đồng lao động', () => void navigate({ to: '/ezy/hr/contract' })],
                  ['Sổ quyết định', 'Về danh sách', back],
                ] as const).map(([k, sub, go]) => (
                  <button key={k} type="button" className={s.link} onClick={go}>
                    <IconFileText size={18} stroke={1.9} aria-hidden="true" />
                    <span><span>{k}</span><span>{sub}</span></span>
                    <IconChevronRight size={16} aria-hidden="true" />
                  </button>
                ))}
              </Card>
            </div>
          </div>
        )}
      </StateView>
    </Page>
  );
}
